import { Fragment, useMemo, useState } from 'react'
import { ChevronRight, Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  useSbaCalendarEventDates,
  useSbaCalendarEventDetail,
} from '@/hooks/useSbaCalendars'
import type { SbaCalendarEventDateRead } from '@/api/clt.types'
import type { EventFilters } from './EventFiltersBar'

interface EventDatesTableProps {
  calendarId: string
  eventId: string
  filters: EventFilters
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function parseIsoDate(
  iso: string | null | undefined
): { y: number; m: number; d: number } | null {
  // Treat as plain calendar date — backend ships yyyy-mm-dd, no TZ.
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  if (!y) return null
  return { y, m: (m ?? 1) - 1, d: d ?? 1 }
}

function fmtLong(iso: string | null | undefined) {
  const parts = parseIsoDate(iso)
  if (!parts) return '—'
  return `${MONTHS[parts.m]} ${parts.d}, ${parts.y}`
}

function fmtShort(iso: string | null | undefined) {
  const parts = parseIsoDate(iso)
  if (!parts) return '—'
  return `${MONTHS[parts.m]} ${parts.d}`
}

function rangeLong(
  min: string | null | undefined,
  max: string | null | undefined
) {
  if (!min || !max) return '—'
  if (min === max) return fmtLong(min)
  const minParts = parseIsoDate(min)
  const maxParts = parseIsoDate(max)
  if (minParts && maxParts && minParts.y === maxParts.y) {
    return `${fmtShort(min)} – ${fmtLong(max)}`
  }
  return `${fmtLong(min)} – ${fmtLong(max)}`
}

interface MilestoneGroup {
  milestoneId: string
  label: string
  min: string
  max: string
}

export function EventDatesTable({
  calendarId,
  eventId,
  filters,
}: EventDatesTableProps) {
  const {
    data: detail,
    isLoading,
    isError,
  } = useSbaCalendarEventDetail(calendarId, eventId)

  const groups = useMemo<MilestoneGroup[]>(() => {
    const milestones = detail?.milestones ?? []
    return [...milestones]
      .filter(
        (
          m
        ): m is typeof m & {
          computed_date_min: string
          computed_date_max: string
        } => !!m.computed_date_min && !!m.computed_date_max
      )
      .sort((a, b) => a.display_order - b.display_order)
      .map(m => ({
        milestoneId: m.id,
        label: m.name,
        min: m.computed_date_min,
        max: m.computed_date_max,
      }))
  }, [detail])

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading dates…
      </div>
    )
  }

  if (isError) {
    return (
      <p className="rounded-lg border border-dashed border-destructive/40 p-6 text-center text-sm text-destructive">
        Failed to load dates for this event.
      </p>
    )
  }

  if (groups.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No dates computed for this event yet.
      </p>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/50">
          <tr className="text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 text-left font-semibold">Milestone</th>
            <th className="px-3 py-2 text-left font-semibold">Date (range)</th>
            <th className="w-10 px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {groups.map(group => {
            const isOpen = expanded.has(group.milestoneId)
            const single = group.min === group.max
            return (
              <Fragment key={group.milestoneId}>
                <tr
                  className="cursor-pointer border-t border-border/60 hover:bg-muted/30"
                  onClick={() => toggle(group.milestoneId)}
                >
                  <td className="px-3 py-2 font-medium text-foreground">
                    {group.label}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium tabular-nums',
                        single
                          ? 'bg-muted text-foreground'
                          : 'bg-blue-50 text-blue-700'
                      )}
                    >
                      {rangeLong(group.min, group.max)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 text-muted-foreground transition-transform',
                        isOpen && 'rotate-90'
                      )}
                    />
                  </td>
                </tr>
                {isOpen && (
                  <tr className="border-t border-border/60 bg-muted/20">
                    <td colSpan={3} className="px-3 py-3">
                      <MilestoneDrilldown
                        calendarId={calendarId}
                        eventId={eventId}
                        milestoneId={group.milestoneId}
                        filters={filters}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

interface MilestoneDrilldownProps {
  calendarId: string
  eventId: string
  milestoneId: string
  filters: EventFilters
}

function MilestoneDrilldown({
  calendarId,
  eventId,
  milestoneId,
  filters,
}: MilestoneDrilldownProps) {
  const params = useMemo(
    () => ({ milestone_id: milestoneId, ...filters }),
    [milestoneId, filters]
  )
  const {
    data: response,
    isLoading,
    isError,
  } = useSbaCalendarEventDates(calendarId, eventId, params)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading dates…
      </div>
    )
  }

  if (isError) {
    return (
      <p className="py-4 text-center text-xs text-destructive">
        Failed to load dates for this milestone.
      </p>
    )
  }

  const rows = response?.data ?? []
  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">
        No dates match the current filters.
      </p>
    )
  }

  return <DrilldownTable rows={rows} />
}

interface DrilldownTableProps {
  rows: SbaCalendarEventDateRead[]
}

function DrilldownTable({ rows }: DrilldownTableProps) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.date.localeCompare(b.date)),
    [rows]
  )
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <table className="w-full border-collapse text-xs">
        <thead className="bg-muted/40">
          <tr className="text-muted-foreground">
            <th className="px-2.5 py-1.5 text-left font-medium">Strategy</th>
            <th className="px-2.5 py-1.5 text-left font-medium">Sourcing</th>
            <th className="px-2.5 py-1.5 text-left font-medium">Origin port</th>
            <th className="px-2.5 py-1.5 text-left font-medium">DC</th>
            <th className="px-2.5 py-1.5 text-left font-medium">Fiscal wk</th>
            <th className="px-2.5 py-1.5 text-left font-medium">Date</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={`${row.date}-${row.origin_port}-${row.dc}-${i}`}
              className="border-t border-border/50 text-foreground"
            >
              <td className="px-2.5 py-1.5">
                {row.strategy_groups?.length
                  ? row.strategy_groups.map(g => g.label).join(', ')
                  : '—'}
              </td>
              <td className="px-2.5 py-1.5">
                {row.is_import ? 'Imp' : 'Dom'}·{row.is_rail ? 'R' : 'N'}
              </td>
              <td className="px-2.5 py-1.5">{row.origin_port}</td>
              <td className="px-2.5 py-1.5">{row.dc}</td>
              <td className="px-2.5 py-1.5 tabular-nums">
                {row.fiscal_week ?? '—'}
              </td>
              <td className="px-2.5 py-1.5 tabular-nums">
                {fmtLong(row.date)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
